import React from 'react';
import SEO from '../components/common/SEO';
import ContactForm from '../components/contact/ContactForm';
import Card from '../components/common/Card';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaInstagram } from 'react-icons/fa';
import useScrollAnimation from '../hooks/useScrollAnimation';

const Contact = () => {
  // Scroll animations
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <>
      <SEO
        title="Contact - Confident Portraits | Get in Touch"
        description="Get in touch with Confident Portraits for portrait photography, writing services, or social media management. Let's create something beautiful together."
        url="/contact"
      />
      <div className="pt-24 md:pt-28 pb-20 bg-white min-h-screen">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header with fade-in animation */}
          <div
            ref={headerRef}
            className={`text-center mb-16 md:mb-20 transition-all duration-700 ease-out ${
              headerVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-8'
            }`}
          >
            <h1 className="text-4xl md:text-5xl lg:text-display font-heading font-bold text-secondary mb-6 leading-tight">
              Get in Touch
            </h1>
            <p className="text-lg md:text-xl text-text-secondary font-body max-w-2xl mx-auto leading-relaxed">
              Let's create something beautiful together. Reach out to discuss your project.
            </p>
          </div>

          <div ref={contentRef} className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
              {/* Contact Form */}
              <div
                className={`transition-all duration-700 ease-out ${
                  contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
              >
                <Card className="p-8 md:p-10 h-full">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-secondary mb-8">
                    Send a Message
                  </h2>
                  <ContactForm />
                </Card>
              </div>

              {/* Contact Information */}
              <div
                className={`space-y-6 lg:space-y-8 transition-all duration-700 ease-out ${
                  contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: contentVisible ? '150ms' : '0ms' }}
              >
                <Card className="p-8 md:p-10">
                  <h2 className="text-2xl md:text-3xl font-heading font-bold text-secondary mb-8">
                    Contact Information
                  </h2>

                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-accent-light rounded-full flex items-center justify-center flex-shrink-0">
                        <FaEnvelope className="text-accent text-lg" />
                      </div>
                      <div className="flex-1 pt-1">
                        <h3 className="font-heading font-semibold text-secondary mb-2">Email</h3>
                        <a
                          href="mailto:kytracy23@gmail.com"
                          className="text-text-secondary hover:text-accent transition-all duration-300 font-body break-all"
                        >
                          kytracy23@gmail.com
                        </a>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-accent-light rounded-full flex items-center justify-center flex-shrink-0">
                        <FaPhone className="text-accent text-lg" />
                      </div>
                      <div className="flex-1 pt-1">
                        <h3 className="font-heading font-semibold text-secondary mb-2">Phone</h3>
                        <p className="text-text-secondary font-body">Available upon request</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-accent-light rounded-full flex items-center justify-center flex-shrink-0">
                        <FaMapMarkerAlt className="text-accent text-lg" />
                      </div>
                      <div className="flex-1 pt-1">
                        <h3 className="font-heading font-semibold text-secondary mb-2">Location</h3>
                        <p className="text-text-secondary font-body">Elizabethtown, PA & Kennett Square, PA</p>
                      </div>
                    </div>

                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-accent-light rounded-full flex items-center justify-center flex-shrink-0">
                        <FaInstagram className="text-accent text-lg" />
                      </div>
                      <div className="flex-1 pt-1">
                        <h3 className="font-heading font-semibold text-secondary mb-2">Instagram</h3>
                        <a
                          href="https://instagram.com/kytracymedia"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-text-secondary hover:text-accent transition-all duration-300 font-body"
                        >
                          @kytracymedia
                        </a>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-8 md:p-10 bg-gradient-to-br from-accent via-accent to-accent-alt text-white shadow-soft-lg">
                  <h3 className="text-xl md:text-2xl font-heading font-bold mb-4 leading-tight">
                    Ready to Capture Your Confidence?
                  </h3>
                  <div className="space-y-4 font-body text-white/95 leading-relaxed">
                    <p>
                      Whether you're looking for professional portraits, creative writing, or
                      social media management, we're here to bring your vision to life.
                    </p>
                    <p>
                      Fill out the form and we'll get back to you within 24-48 hours.
                    </p>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
